import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { useCallback, useRef, useState } from 'react';
import { Button } from 'framework/ui/mui-builder';
import { DATE_FORMAT } from 'framework/utils';
import { FinalizeProjectReviewData, ProjectReviewResult } from '../types';
import ControlPlaneAccordion from './components/ControlPlaneAccordion';
import StaleItemsList from './components/StaleItemsList';

interface ResultReviewProps {
  owner?: string;
  review: ProjectReviewResult;
  onFinalize: (data: FinalizeProjectReviewData) => Promise<void>;
  onFinalizeSuccess?: () => void;
  onFinalizeError?: () => void;
}

const ResultReview = ({ owner, review, onFinalize, onFinalizeSuccess, onFinalizeError }: ResultReviewProps) => {
  const [openComments, setOpenComments] = useState<Set<string>>(() => new Set());
  const [comments, setComments] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [isFinalized, setIsFinalized] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const isFinalizingRef = useRef(false);

  const handleCommentToggle = useCallback((entityId: string) => {
    setOpenComments(currentOpenComments => {
      const updatedOpenComments = new Set(currentOpenComments);

      if (updatedOpenComments.has(entityId)) updatedOpenComments.delete(entityId);
      else updatedOpenComments.add(entityId);

      return updatedOpenComments;
    });
  }, []);

  const handleCommentChange = useCallback((entityId: string, value: string) => {
    setComments(currentComments => ({
      ...currentComments,
      [entityId]: value,
    }));
  }, []);

  const handleFinalize = async () => {
    if (isFinalized || isFinalizingRef.current) return;

    isFinalizingRef.current = true;
    setIsFinalizing(true);
    let isSuccessfullyFinalized = false;

    try {
      await onFinalize({ note, comments });
      setIsFinalized(true);
      isSuccessfullyFinalized = true;
    } catch {
      onFinalizeError?.();
    } finally {
      isFinalizingRef.current = false;
      setIsFinalizing(false);
    }

    if (isSuccessfullyFinalized) {
      onFinalizeSuccess?.();
    }
  };

  return (
    <Box sx={{ mt: -3 }}>
      <Typography variant="body1" color="textSecondary" sx={{ pb: 4 }}>
        {moment(review.from).format(DATE_FORMAT.FULL_DATE)} — {moment(review.to).format(DATE_FORMAT.FULL_DATE)}
      </Typography>

      <StaleItemsList staleItems={review.violations} />

      {review.sections.map((section, index) => (
        <ControlPlaneAccordion
          key={section.section}
          comments={comments}
          defaultExpanded={index === 0}
          isCommentingDisabled={isFinalized}
          openComments={openComments}
          owner={owner}
          section={section}
          onCommentChange={handleCommentChange}
          onCommentToggle={handleCommentToggle}
        />
      ))}

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Finalization note"
        placeholder="Add optional note"
        value={note}
        disabled={isFinalized}
        onChange={event => setNote(event.target.value)}
        sx={{ mt: 2 }}
      />
      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          startIcon={isFinalizing ? <CircularProgress color="inherit" size={16} /> : undefined}
          disabled={isFinalizing || isFinalized}
          onClick={handleFinalize}
        >
          {isFinalized ? 'Finalized' : 'Finalize review'}
        </Button>
      </Box>
    </Box>
  );
};

export default ResultReview;
